import { Immutable } from "immer";
import React from "react";

import type { ProductDetailsBlock as Value } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";

type Props = Immutable<{
    value: Value;
}>;

/**
 * React port of the pillows-specific ``ProductDetailsBlock`` PDP section.
 * DOM mirrors the legacy Django template
 * ``cms/components/product_details.html`` exactly — headline/description
 * RichText header, a value-props grid (icons rendered via CSS
 * ``background-image``), a main product image, and an optional specs bar.
 *
 * Note: ``headline``/``description`` are RichText (rendered with
 * ``RichTextBlock``); the per-prop ``title``/``description`` and spec
 * ``title``/``info`` are plain CharBlock strings rendered directly. The
 * ``section-info-sleep__copyc`` class preserves a typo present in the
 * legacy template for parity.
 */
export const ProductDetailsBlock = (props: Props): React.JSX.Element => {
    const v = props.value;
    const imageUrl = v.image?.url ?? "";
    const valueProps = v.value_props ?? [];
    const specs = v.info_bar?.specs ?? [];
    return (
        <div
            id="pdp-features"
            className="l-full-width section-info-sleep section-info-sleep--pdp"
        >
            <div className="l-capped-width section-info-sleep__inner section-info-sleep__inner--pdp">
                <div className="section-info-sleep__header">
                    <div className="section-info-sleep__title section-info-sleep__title--pdp">
                        <RichTextBlock value={v.headline ?? ""} />
                    </div>
                    <div className="section-info-sleep__copyc section-info-sleep__copy--pdp">
                        <RichTextBlock value={v.description ?? ""} />
                    </div>
                </div>
                <div className="section-info-sleep__content">
                    <div className="section-info-sleep__value-props-container">
                        {valueProps.map((prop, i) => {
                            const propIconUrl = prop.image?.url ?? "";
                            return (
                                <div
                                    key={i}
                                    className="section-info-sleep__value-props"
                                >
                                    <div className="section-info-sleep__value-props__icon">
                                        <div
                                            className="section-info-sleep__value-props__icon-item"
                                            style={{
                                                backgroundImage: `url(${propIconUrl})`,
                                            }}
                                        ></div>
                                    </div>
                                    <div className="section-info-sleep__value-props__copy">
                                        <h3 className="section-info-sleep__value-props__title">
                                            {prop.title ?? ""}
                                        </h3>
                                        <p className="section-info-sleep__value-props__sub-title">
                                            {prop.description ?? ""}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="section-info-sleep__image-container">
                        <img
                            className="section-info-sleep__image section-info-sleep__image--pdp responsive-img"
                            src={imageUrl}
                            alt={
                                v.image?.default_alt_text ||
                                v.image?.title ||
                                ""
                            }
                            loading="lazy"
                            decoding="async"
                        />
                    </div>
                    {specs.length > 0 && (
                        <div className="section-info-sleep__bar">
                            {specs.map((spec, i) => (
                                <div
                                    key={i}
                                    className="section-info-sleep__bar-spec"
                                >
                                    <div className="section-info-sleep__bar-spec__title">
                                        {spec.title ?? ""}
                                    </div>
                                    <div className="section-info-sleep__bar-spec__desc">
                                        {spec.info ?? ""}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
